import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { useQueryClient, useMutation } from "react-query";
import { useToast } from "../../../hooks/useToast";
import { useCookie } from "../../../hooks/useCookie";
import apiService from "../../../service/apiService";
import { DELETE_ACTIONS } from "../../../config/constants";
import { general } from "../../../locales/general";
import { colors } from "../../../config/theme";
import EditIcon from "../../../components/icons/EditIcon";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import PromptModal from "../../../components/modals/PromptModal";
import { Link } from "react-router-dom";

interface RewardTranslation {
  id: string;
  reward: Reward;
  title: string;
  lang: string;
  image: string;
}

function TableBodyRowContent({
  rewardTranslation,
  deletedList,
}: {
  rewardTranslation: RewardTranslation;
  deletedList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();

  const deleteRewardTranslation = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`rewards-translations/${id}`, token);
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: rewardTranslation.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: rewardTranslation.id });
    }
  }, [deletedList, isChecked, rewardTranslation.id]);

  const removeRewardTranslation = () => {
    deleteRewardTranslation.mutate(rewardTranslation.id, {
      onError(error) {
        console.log(error);
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.rewardTranslationDeleted);
        queryClient.invalidateQueries(["getAllTranslations"]);
      },
    });
  };

  return (
    <CTableRow
      style={{
        borderLeft: isChecked ? `3px solid ${colors.primary}` : "",
        width: "100%",
        position: "relative",
      }}
    >
      <CTableDataCell className="text-capitalize text-sm">
        <div className="d-flex align-items-center gap-3">
          <CFormCheck
            label=""
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            color={colors.yellow}
          />
          <img
            src={rewardTranslation?.image || "/placeholder.png"}
            alt={rewardTranslation?.title}
            width={30}
            height={30}
            style={{ borderRadius: "100%", objectFit: "cover" }}
          />
          <div>{rewardTranslation?.reward?.title}</div>
        </div>
      </CTableDataCell>

      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">{rewardTranslation?.title}</span>
      </CTableDataCell>

      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">{rewardTranslation?.lang?.toUpperCase()}</span>
      </CTableDataCell>

      <CTableDataCell className="text-end">
        <Link to={`/rewards-translations/update/${rewardTranslation.id}`}>
          <EditIcon width="18" height="18" fill={colors.secondary} />
        </Link>{" "}
        <button
          className="outline-none border-0 bg-transparent"
          onClick={() => setShowModal(true)}
          disabled={deleteRewardTranslation.isLoading}
        >
          {deleteRewardTranslation.isLoading ? (
            <CSpinner size="sm" />
          ) : (
            <DeleteIcon
              width="20"
              height="20"
              fill={colors.red}
              className="ms-2"
            />
          )}
        </button>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={removeRewardTranslation}
        onClose={() => setShowModal(false)}
        message={general.fr.message.deleteRewardTranslationConfirm}
        loading={deleteRewardTranslation.isLoading}
      />
    </CTableRow>
  );
}

// 🔹 Main Table Component
interface TableProps {
  rewardTranslations: RewardTranslation[];
  rewardTranslationsToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function RewardsTranslationsTable({
  rewardTranslations,
  rewardTranslationsToBeDeleted,
}: TableProps) {
  if (!rewardTranslations?.length) {
    return (
      <div className="text-center py-4 text-muted">
        {general.fr.message.noRewardTranslations}
      </div>
    );
  }

  return (
    <div className="mt-3">
      <CTable responsive hover align="middle">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col">Bonus</CTableHeaderCell>
            <CTableHeaderCell scope="col">Titre</CTableHeaderCell>
            <CTableHeaderCell scope="col">Langue</CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-end">
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {rewardTranslations.map((rewardTranslation) => (
            <TableBodyRowContent
              rewardTranslation={rewardTranslation}
              key={rewardTranslation.id}
              deletedList={rewardTranslationsToBeDeleted}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
